import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import Conversation from '../../components/shared/Conversation';
import { listClients, clientLabel } from '../../utils/clients';
import { watchConversation, sendMessage, markAllReadByAdmin } from '../../utils/messages';
import * as ds from '../../constants/dashboardStyles';

const styles = {
  card: ds.card,
  title: ds.cardTitle,
  description: ds.cardDescription,
  select: { ...ds.select, marginBottom: 20 },
  empty: ds.empty,
  error: ds.error,
  unreadRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 16,
  },
  unreadChip: {
    padding: '6px 10px',
    borderRadius: 999,
    border: '1px solid #f0c9c3',
    background: '#fdf0ee',
    color: '#c0392b',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
};

// `unread` is passed in from AdminScreen, which already watches it for the tab
// badge — running the same query again here would double the reads.
export default function MessagesPanel({ unread = [] }) {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState('');
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    listClients()
      .then(setClients)
      .catch((err) => setError(err.message.replace('Firebase: ', '')));
  }, []);

  useEffect(() => {
    if (!selected) {
      setMessages([]);
      return undefined;
    }
    return watchConversation(
      selected,
      setMessages,
      (err) => setError(err.message.replace('Firebase: ', '')),
    );
  }, [selected]);

  // Opening a client's thread clears their unread count.
  useEffect(() => {
    const ids = messages
      .filter((message) => message.from === 'client' && !message.readByAdmin)
      .map((message) => message.id);
    markAllReadByAdmin(ids).catch(() => {});
  }, [messages]);

  // One chip per client with waiting questions, so you can jump straight to
  // whoever needs answering instead of hunting through the dropdown.
  const waitingClients = [...new Set(unread.map((message) => message.clientEmail))];

  const handleSend = (body) => sendMessage({
    clientEmail: selected,
    from: 'admin',
    body,
    authorEmail: user.email,
  });

  return (
    <div style={styles.card}>
      <div style={styles.title}>Messages</div>
      <p style={styles.description}>Questions clients have sent, and your replies. They see these on their account page.</p>

      {error && <div style={styles.error}>{error}</div>}

      {waitingClients.length > 0 && (
        <div style={styles.unreadRow}>
          {waitingClients.map((email) => (
            <button key={email} type="button" style={styles.unreadChip} onClick={() => setSelected(email)}>
              {email} · {unread.filter((m) => m.clientEmail === email).length} waiting
            </button>
          ))}
        </div>
      )}

      <select style={styles.select} value={selected} onChange={(e) => setSelected(e.target.value)}>
        <option value="">Select a client…</option>
        {clients.map((client) => (
          <option key={client.id} value={client.email}>{clientLabel(client)}</option>
        ))}
      </select>

      {!selected ? (
        <p style={styles.empty}>Pick a client to see their conversation.</p>
      ) : (
        <Conversation
          messages={messages}
          side="admin"
          onSend={handleSend}
          placeholder={`Reply to ${selected}…`}
          emptyText="Nothing from this client yet — you can start the conversation."
        />
      )}
    </div>
  );
}
